import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Edit2, Trash2, X, Save, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", description: "", image: "", parent_id: "", order: 0 };

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/categories").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const onDelete = async (c) => {
        if (!window.confirm(`¿Eliminar la categoría "${c.name}"? Los productos quedarán sin categoría.`)) return;
        try {
            await api.delete(`/categories/${c.id}`);
            toast.success("Categoría eliminada");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const move = async (idx, dir) => {
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= items.length) return;
        const next = [...items];
        const [it] = next.splice(idx, 1);
        next.splice(newIdx, 0, it);
        setItems(next);
        try {
            await api.post("/categories/reorder", { ids: next.map((c) => c.id) });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
            load();
        }
    };

    return (
        <div className="p-6 md:p-10" data-testid="admin-categories-page">
            <div className="border-b-2 border-[var(--lz-navy)] pb-4 mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="lz-section-label">Departamentos</span>
                    <h1 className="font-display text-4xl font-bold uppercase">Categorías</h1>
                </div>
                <button onClick={() => setEditing({ ...empty })} className="lz-btn lz-btn-primary" data-testid="new-cat-btn">
                    <Plus className="w-4 h-4" /> Nueva
                </button>
            </div>

            <div className="border-2 border-[var(--lz-navy)] bg-white">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[var(--lz-navy)] text-white text-[10px] uppercase tracking-widest">
                            <th className="p-3"></th>
                            <th className="p-3 text-left">Nombre</th>
                            <th className="p-3 text-left">Slug</th>
                            <th className="p-3">Padre</th>
                            <th className="p-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#0f1419]/15">
                        {items.map((c, idx) => (
                            <tr key={c.id} data-testid={`admin-cat-row-${c.slug}`}>
                                <td className="p-2 w-16">
                                    <div className="flex flex-col items-center gap-0.5">
                                        <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-xs disabled:opacity-30 hover:text-[var(--lz-orange)]" data-testid={`cat-up-${c.slug}`}><ChevronUp className="w-3 h-3" /></button>
                                        <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-xs disabled:opacity-30 hover:text-[var(--lz-orange)]" data-testid={`cat-down-${c.slug}`}><ChevronDown className="w-3 h-3" /></button>
                                    </div>
                                </td>
                                <td className="p-3 font-bold">{c.name}</td>
                                <td className="p-3 font-mono text-xs">{c.slug}</td>
                                <td className="p-3 text-xs text-center">{items.find((p) => p.id === c.parent_id)?.name || "—"}</td>
                                <td className="p-3">
                                    <div className="flex gap-1 justify-center">
                                        <button onClick={() => setEditing(c)} className="p-2 border border-[var(--lz-navy)] hover:bg-[var(--lz-orange)]" data-testid={`edit-cat-${c.slug}`}><Edit2 className="w-3 h-3" /></button>
                                        <button onClick={() => onDelete(c)} className="p-2 border border-[var(--lz-navy)] hover:bg-red-600 hover:text-white" data-testid={`delete-cat-${c.slug}`}><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={5} className="p-10 text-center text-[var(--lz-navy)]/60">Sin categorías</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <CategoryDialog
                    initial={editing}
                    parents={items.filter((c) => c.id !== editing.id)}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function CategoryDialog({ initial, parents, onClose, onSaved }) {
    const isNew = !initial.id;
    const [form, setForm] = useState({
        name: initial.name || "",
        description: initial.description || "",
        image: initial.image || "",
        parent_id: initial.parent_id || "",
        order: initial.order || 0,
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form, parent_id: form.parent_id || null };
            if (isNew) {
                await api.post("/categories", payload);
                toast.success("Categoría creada");
            } else {
                await api.put(`/categories/${initial.id}`, payload);
                toast.success("Categoría actualizada");
            }
            onSaved();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4" data-testid="cat-dialog">
            <div className="bg-white border-2 border-[var(--lz-navy)] w-full max-w-xl my-8">
                <div className="flex items-center justify-between bg-[var(--lz-navy)] text-white px-5 py-3">
                    <h2 className="font-display font-bold uppercase">{isNew ? "Nueva Categoría" : "Editar Categoría"}</h2>
                    <button onClick={onClose} className="p-1 hover:text-[var(--lz-orange)]"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Nombre *</label>
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lz-input" data-testid="cat-name" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Descripción</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="lz-input min-h-[100px]" data-testid="cat-description" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Imagen (URL)</label>
                        <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="lz-input" placeholder="https://..." data-testid="cat-image" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Categoría padre</label>
                        <select value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="lz-input" data-testid="cat-parent">
                            <option value="">Ninguna (raíz)</option>
                            {parents.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="flex gap-2 justify-end pt-2 border-t-2 border-[var(--lz-navy)]">
                        <button type="button" onClick={onClose} className="lz-btn lz-btn-ghost">Cancelar</button>
                        <button type="submit" disabled={busy} className="lz-btn lz-btn-primary" data-testid="cat-save">
                            <Save className="w-4 h-4" /> {busy ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
